// Forecast engine: pure functions. All calculations per Avyntis spec.

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeCriticality = (c, fallback = "Medium") => {
  if (!c) return fallback;
  const s = String(c).trim().toLowerCase();
  if (s.startsWith("h")) return "High";
  if (s.startsWith("l")) return "Low";
  if (s.startsWith("m")) return "Medium";
  return fallback;
};

const safetyFactorFor = (crit, settings) => {
  if (crit === "High") return settings.safetyHigh;
  if (crit === "Low") return settings.safetyLow;
  return settings.safetyMedium;
};

// Pull the average monthly usage from monthly demand columns, falling back to historical.
function _averageMonthlyUsage(r) {
  const monthly = [
    "MonthlyDemandMonth1",
    "MonthlyDemandMonth2",
    "MonthlyDemandMonth3",
    "MonthlyDemandMonth4",
    "MonthlyDemandMonth5",
    "MonthlyDemandMonth6",
  ]
    .map((k) => r[k])
    .filter((v) => v !== undefined && v !== null && v !== "")
    .map(toNum);
  if (monthly.length > 0) return monthly.reduce((a, b) => a + b, 0) / monthly.length;
  return toNum(r.HistoricalMonthlyUsage);
}

// Detect slow-moving by either explicit date threshold or low usage.
function _isSlowMoving(r, currentStock, avgMonthly, slowThreshold) {
  if (r.LastIssueDate) {
    const d = new Date(r.LastIssueDate);
    if (!isNaN(d.getTime())) {
      const daysSince = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
      return daysSince >= slowThreshold && currentStock > 0;
    }
    return false;
  }
  return avgMonthly < 1 && currentStock > 0;
}

// Bucket the item into a single user-facing status.
function _statusFor(stockoutRisk, overstockQty, slowMoving) {
  if (stockoutRisk === "High" || stockoutRisk === "Medium") return "Stockout Risk";
  if (overstockQty > 0) return "Overstock";
  if (slowMoving) return "Slow Moving";
  return "Healthy";
}

export function computeRow(r, settings) {
  const averageMonthlyUsage = _averageMonthlyUsage(r);

  const leadTimeDays = toNum(r.LeadTimeDays);
  const leadTimeDemand = (averageMonthlyUsage / 30) * leadTimeDays;

  const criticality = normalizeCriticality(r.Criticality, settings.defaultCriticality);
  const safetyStock = averageMonthlyUsage * safetyFactorFor(criticality, settings);

  const maintenancePlanQty = toNum(r.MaintenancePlanQty);
  const horizonFactor = (settings.forecastHorizon || 30) / 30;
  const forecastDemand =
    averageMonthlyUsage * horizonFactor + leadTimeDemand + safetyStock + maintenancePlanQty;

  const reorderPoint = leadTimeDemand + safetyStock;

  const currentStock = toNum(r.CurrentStock);
  const openPOQty = toNum(r.OpenPOQty);
  const available = currentStock + openPOQty;

  const recommendedOrderQty =
    available < reorderPoint ? Math.max(0, reorderPoint - available) : 0;

  let stockoutRisk = "Low";
  if (available < leadTimeDemand) stockoutRisk = "High";
  else if (available < reorderPoint) stockoutRisk = "Medium";

  const maxStock = toNum(r.MaxStock);
  const overstockQty = currentStock > maxStock ? currentStock - maxStock : 0;
  const unitCost = toNum(r.UnitCost);
  const overstockValue = overstockQty * unitCost;
  const reorderValue = recommendedOrderQty * unitCost;
  const inventoryValue = currentStock * unitCost;

  const slowMoving = _isSlowMoving(
    r, currentStock, averageMonthlyUsage, settings.slowMovingDays || 180
  );
  const status = _statusFor(stockoutRisk, overstockQty, slowMoving);

  return {
    ...r,
    Criticality: criticality,
    AverageMonthlyUsage: round(averageMonthlyUsage),
    LeadTimeDemand: round(leadTimeDemand),
    SafetyStock: round(safetyStock),
    ForecastDemand: round(forecastDemand),
    ReorderPoint: round(reorderPoint),
    RecommendedOrderQty: round(recommendedOrderQty),
    StockoutRisk: stockoutRisk,
    OverstockQty: round(overstockQty),
    OverstockValue: round(overstockValue, 2),
    ReorderValue: round(reorderValue, 2),
    InventoryValue: round(inventoryValue, 2),
    SlowMoving: slowMoving,
    Status: status,
  };
}

const round = (n, d = 0) => {
  const f = Math.pow(10, d);
  return Math.round(n * f) / f;
};

export function computeForecast(rows, settings) {
  const results = (rows || []).map((r) => computeRow(r, settings));

  const totalSKUs = results.length;
  const totalForecastDemand = results.reduce((s, r) => s + r.ForecastDemand, 0);
  const stockoutRiskItems = results.filter(
    (r) => r.StockoutRisk === "High" || r.StockoutRisk === "Medium"
  ).length;
  const overstockItems = results.filter((r) => r.OverstockQty > 0).length;
  const slowMovingItems = results.filter((r) => r.SlowMoving).length;
  const healthyItems = results.filter((r) => r.Status === "Healthy").length;
  const reorderValue = results.reduce((s, r) => s + r.ReorderValue, 0);
  const overstockValue = results.reduce((s, r) => s + r.OverstockValue, 0);
  const workingCapitalAtRisk = reorderValue + overstockValue;

  return {
    rows: results,
    kpi: {
      totalSKUs,
      totalForecastDemand: round(totalForecastDemand),
      stockoutRiskItems,
      overstockItems,
      slowMovingItems,
      healthyItems,
      reorderValue: round(reorderValue, 2),
      overstockValue: round(overstockValue, 2),
      workingCapitalAtRisk: round(workingCapitalAtRisk, 2),
    },
  };
}

export function groupSum(rows, key, valueKey) {
  const map = new Map();
  rows.forEach((r) => {
    const k = r[key] || "Unknown";
    map.set(k, (map.get(k) || 0) + (r[valueKey] || 0));
  });
  return Array.from(map, ([name, value]) => ({ name, value: round(value, 2) }));
}

export function groupCount(rows, key, predicate) {
  const map = new Map();
  rows.forEach((r) => {
    if (predicate && !predicate(r)) return;
    const k = r[key] || "Unknown";
    map.set(k, (map.get(k) || 0) + 1);
  });
  return Array.from(map, ([name, value]) => ({ name, value }));
}

export const REQUIRED_FIELDS = [
  "Site",
  "SKU",
  "ItemName",
  "Category",
  "Unit",
  "CurrentStock",
  "MinStock",
  "MaxStock",
  "HistoricalMonthlyUsage",
  "LeadTimeDays",
  "SupplierName",
  "UnitCost",
];

export const OPTIONAL_FIELDS = [
  "Criticality",
  "LastPurchaseDate",
  "LastIssueDate",
  "OpenPOQty",
  "MonthlyDemandMonth1",
  "MonthlyDemandMonth2",
  "MonthlyDemandMonth3",
  "MonthlyDemandMonth4",
  "MonthlyDemandMonth5",
  "MonthlyDemandMonth6",
  "MaintenancePlanQty",
  "PlannedWorkOrders",
  "SupplierOTD",
  "SupplierFillRate",
];

export const NUMERIC_FIELDS = new Set([
  "CurrentStock",
  "MinStock",
  "MaxStock",
  "HistoricalMonthlyUsage",
  "LeadTimeDays",
  "UnitCost",
  "OpenPOQty",
  "MonthlyDemandMonth1",
  "MonthlyDemandMonth2",
  "MonthlyDemandMonth3",
  "MonthlyDemandMonth4",
  "MonthlyDemandMonth5",
  "MonthlyDemandMonth6",
  "MaintenancePlanQty",
  "PlannedWorkOrders",
  "SupplierOTD",
  "SupplierFillRate",
]);
